/**
 * Pure helpers for the host marker that tells the app whether it is
 * running inside a Microsoft Office add-in (Excel today; Word/PowerPoint/
 * Outlook later) or as a regular web app.
 *
 * The host marker is stamped by the Office add-in manifest via a `?surface=`
 * query param on the bootstrap URL (`/office-addin/taskpane?surface=excel-add-in`).
 * The bootstrap page validates and persists it in two places:
 *
 *   1. A `magnus-host` cookie (read by the server in RSC, proxy.ts, and
 *      the (app) layout to skip the SSO redirect for in-pane navigation).
 *   2. `sessionStorage["magnus-host"]` (read by client code that runs
 *      before React context is available — e.g. fetch wrappers that need
 *      to inject the Bearer header).
 *
 * Once persisted, the marker survives any in-pane navigation, so when the
 * task pane navigates to `/chat/abc` or `/workspaces`, the surface stays
 * known on both client and server without re-running the bootstrap.
 *
 * This module is environment-agnostic — pure utilities that work on
 * client and server. For the React context + hook see
 * {@link ./host-context}, and for server-only helpers (cookies()) see
 * {@link ./host-server}.
 */

import { type AppSurface, appSurfaceValues } from "@workspace/services/domain";

export type {
  AppSurface,
  EmbeddedSurface,
  OfficeAddinSurface,
} from "@workspace/services/domain";
export { isEmbeddedSurface, isOfficeAddin } from "@workspace/services/domain";

/**
 * Cookie + sessionStorage key. Same key on both sides so log lines line
 * up across server and client.
 */
export const HOST_MARKER_KEY = "magnus-host";

/**
 * Runtime guard for any string that's claimed to be an `AppSurface`.
 * Returns the value when it's in `appSurfaceValues`, otherwise `null`.
 *
 * Used by the bootstrap page (validating `?surface=`), the
 * `/api/host/mark` route handler, and `getServerHost()` so we never trust
 * an unrecognised cookie value.
 */
export function parseAppSurface(
  value: string | null | undefined
): AppSurface | null {
  if (!value) {
    return null;
  }
  return (appSurfaceValues as readonly string[]).includes(value)
    ? (value as AppSurface)
    : null;
}

/**
 * Reads the host marker from `sessionStorage`. SSR-safe — returns
 * `"standalone-web"` on the server (where `sessionStorage` is undefined).
 *
 * Useful for client-only helpers that run outside the React tree, such
 * as the Bearer-token fetch wrapper in `office-addin-session.ts`.
 */
export function getClientHost(): AppSurface {
  if (typeof window === "undefined") {
    return "standalone-web";
  }
  try {
    const raw = window.sessionStorage.getItem(HOST_MARKER_KEY);
    return parseAppSurface(raw) ?? "standalone-web";
  } catch {
    return "standalone-web";
  }
}

/**
 * Persists the host marker to `sessionStorage`. No-op on the server.
 */
export function setClientHost(surface: AppSurface): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(HOST_MARKER_KEY, surface);
  } catch {
    // sessionStorage may be unavailable (private mode, etc.) — silently
    // ignore; the cookie marker is the source of truth for navigation
    // anyway.
  }
}

/**
 * Removes the host marker from `sessionStorage`. Used on logout.
 */
export function clearClientHost(): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.removeItem(HOST_MARKER_KEY);
  } catch {
    // ignore
  }
}
